import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { api, MessagesPageResponse } from '@/services/api';
import { Message } from '@/types';
import { queryClient } from './queryClient';

export interface UseLeadMessagesOptions {
  userId?: string | null;
  lojaId?: string | null;
  tenantId?: string | null;
  filters?: Record<string, any>;
  signal?: AbortSignal;
}

export function buildMessagesQueryKey(leadId: string, options?: UseLeadMessagesOptions) {
  return [
    'leads',
    leadId,
    'messages',
    {
      userId: options?.userId || null,
      lojaId: options?.lojaId || null,
      tenantId: options?.tenantId || null,
      ...(options?.filters || {})
    }
  ] as const;
}

// Comparador determinístico de timestamps com suporte a microssegundos (ISO 8601)
export function compareTimestamps(dateA?: string | null, dateB?: string | null): number {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1; // NULLS LAST
  if (!dateB) return -1; // NULLS LAST

  const timeA = new Date(dateA).getTime();
  const timeB = new Date(dateB).getTime();

  if (isNaN(timeA) && isNaN(timeB)) {
    return String(dateA).localeCompare(String(dateB));
  }
  if (isNaN(timeA)) return 1;
  if (isNaN(timeB)) return -1;

  if (timeA !== timeB) {
    return timeA - timeB; // ASC
  }

  // Quando os milissegundos são idênticos, extrai a fração de segundos (microssegundos/nanossegundos)
  const matchA = dateA.match(/\.(\d+)/);
  const matchB = dateB.match(/\.(\d+)/);

  if (matchA || matchB) {
    const fracA = (matchA ? matchA[1] : '').padEnd(9, '0').slice(0, 9);
    const fracB = (matchB ? matchB[1] : '').padEnd(9, '0').slice(0, 9);
    if (fracA !== fracB) {
      return fracA.localeCompare(fracB);
    }
  }

  return 0;
}

// Comparador determinístico: created_at ASC NULLS LAST, id ASC
export function compareMessages(a: Message, b: Message): number {
  const timeDiff = compareTimestamps(a.created_at, b.created_at);
  if (timeDiff !== 0) {
    return timeDiff;
  }
  return (a.id || '').localeCompare(b.id || '');
}

export interface MutationRecord {
  id: string;
  type: 'insert' | 'update' | 'delete';
  seq: number;
  data?: any;
}

// ============================================================================
// Registro de Lifecycle, Geração e Revogação por Contexto
// ============================================================================
export interface ContextLifecycle {
  generation: number;
  isRevoked: boolean;
  status?: number;
  message?: string;
}

let globalGenerationCounter = 1;
let globalRecoveryCounter = 0;
const contextLifecycleMap = new Map<string, ContextLifecycle>();
const activeRecoveryByKey = new Map<string, number>();

// Mutações e reconciliação temporal
const mutationSeqByKey = new Map<string, number>();
const mutationsByKey = new Map<string, Map<string, MutationRecord>>();
const tombstonesByKey = new Map<string, Set<string>>();
const activeFetchesByKey = new Map<string, Map<number, number>>();
let globalFetchIdCounter = 0;

export function getContextLifecycle(storageKey: string): ContextLifecycle {
  let ctx = contextLifecycleMap.get(storageKey);
  if (!ctx) {
    ctx = { generation: ++globalGenerationCounter, isRevoked: false };
    contextLifecycleMap.set(storageKey, ctx);
  }
  return ctx;
}

export function isContextRevoked(storageKey: string): boolean {
  const ctx = contextLifecycleMap.get(storageKey);
  return Boolean(ctx?.isRevoked);
}

export function isOperationValid(storageKey: string, expectedGeneration?: number): boolean {
  const ctx = contextLifecycleMap.get(storageKey);
  if (!ctx) return false;
  if (ctx.isRevoked) return false;
  if (expectedGeneration !== undefined && ctx.generation !== expectedGeneration) return false;
  return true;
}

export function isCallbackActive(storageKey: string, callbackGeneration: number): boolean {
  return isOperationValid(storageKey, callbackGeneration);
}

export function revokeContext(
  storageKey: string,
  status: number,
  message: string,
  leadId?: string
): ContextLifecycle {
  let ctx = contextLifecycleMap.get(storageKey);
  if (!ctx) {
    ctx = { generation: ++globalGenerationCounter, isRevoked: true, status, message };
    contextLifecycleMap.set(storageKey, ctx);
  } else {
    ctx.isRevoked = true;
    ctx.status = status;
    ctx.message = message;
    ctx.generation = ++globalGenerationCounter;
  }

  // Limpa apenas mutações temporais, PRESERVANDO o registro de revogação no contextLifecycleMap
  clearMutationTracking(leadId || storageKey);

  return ctx;
}

export function resetContextAuthorization(storageKey: string): ContextLifecycle {
  const ctx: ContextLifecycle = {
    generation: ++globalGenerationCounter,
    isRevoked: false,
    status: undefined,
    message: undefined
  };
  contextLifecycleMap.set(storageKey, ctx);
  return ctx;
}

/**
 * Limpa tracking de mutações e invalida gerações de lifecycle.
 * Na limpeza de logout, marca explicitamente todos os contextos como revogados
 * e incrementa a geração global, impedindo callbacks e respostas antigas.
 */
export function clearMutationTracking(leadIdOrKey?: string) {
  if (leadIdOrKey) {
    for (const key of Array.from(mutationsByKey.keys())) {
      if (key.includes(leadIdOrKey) || (leadIdOrKey.startsWith('[') && key === leadIdOrKey)) {
        mutationsByKey.delete(key);
        mutationSeqByKey.delete(key);
        activeFetchesByKey.delete(key);
        tombstonesByKey.delete(key);
        activeRecoveryByKey.delete(key);
      }
    }
  } else {
    // Logout / limpeza geral: invalida todos os lifecycles existentes
    for (const ctx of contextLifecycleMap.values()) {
      ctx.isRevoked = true;
      ctx.generation = ++globalGenerationCounter;
    }
    mutationsByKey.clear();
    mutationSeqByKey.clear();
    activeFetchesByKey.clear();
    tombstonesByKey.clear();
    activeRecoveryByKey.clear();
    globalGenerationCounter += 1000;
  }
}

export function recordMutation(
  storageKey: string,
  id: string,
  type: 'insert' | 'update' | 'delete' | 'rollback_delete',
  data?: any
): number {
  if (isContextRevoked(storageKey)) {
    return -1;
  }

  const currentSeq = (mutationSeqByKey.get(storageKey) || 0) + 1;
  mutationSeqByKey.set(storageKey, currentSeq);

  let map = mutationsByKey.get(storageKey);
  if (!map) {
    map = new Map<string, MutationRecord>();
    mutationsByKey.set(storageKey, map);
  }

  let tombstones = tombstonesByKey.get(storageKey);
  if (!tombstones) {
    tombstones = new Set<string>();
    tombstonesByKey.set(storageKey, tombstones);
  }

  if (type === 'delete') {
    map.set(id, { id, type: 'delete', seq: currentSeq, data: null });
    tombstones.add(id);
  } else if (type === 'rollback_delete') {
    // Rollback autorizado após falha de exclusão otimista: remove o tombstone
    tombstones.delete(id);
    if (data) {
      map.set(id, { id, type: 'insert', seq: currentSeq, data });
    } else {
      map.delete(id);
    }
  } else if (type === 'update') {
    if (tombstones.has(id)) {
      return currentSeq; // Não ressuscita deletado
    }
    const existing = map.get(id);
    if (existing && existing.type === 'delete') {
      return currentSeq;
    }
    if (existing && existing.type === 'insert') {
      const merged = { ...existing.data, ...data };
      map.set(id, { id, type: 'insert', seq: currentSeq, data: merged });
    } else if (existing && existing.type === 'update') {
      const merged = { ...existing.data, ...data };
      map.set(id, { id, type: 'update', seq: currentSeq, data: merged });
    } else {
      map.set(id, { id, type: 'update', seq: currentSeq, data });
    }
  } else if (type === 'insert') {
    if (tombstones.has(id)) {
      // DELETE confirmado impede que INSERT atrasado ressuscite mensagem
      return currentSeq;
    }
    const existing = map.get(id);
    if (existing && existing.type === 'delete') {
      return currentSeq;
    }
    map.set(id, { id, type: 'insert', seq: currentSeq, data });
  }

  return currentSeq;
}

export function startFetchTracking(storageKey: string): { fetchId: number; startSeq: number } {
  const fetchId = ++globalFetchIdCounter;
  const startSeq = mutationSeqByKey.get(storageKey) || 0;
  let fetches = activeFetchesByKey.get(storageKey);
  if (!fetches) {
    fetches = new Map<number, number>();
    activeFetchesByKey.set(storageKey, fetches);
  }
  fetches.set(fetchId, startSeq);
  return { fetchId, startSeq };
}

export function finishFetchTracking(storageKey: string, fetchId: number, startSeq: number) {
  const fetches = activeFetchesByKey.get(storageKey);
  if (fetches) {
    fetches.delete(fetchId);
  }

  const map = mutationsByKey.get(storageKey);
  if (!map || map.size === 0) return;

  let minActiveSeq = Infinity;
  if (fetches && fetches.size > 0) {
    for (const seq of fetches.values()) {
      if (seq < minActiveSeq) minActiveSeq = seq;
    }
  }

  const maxSafeSeqToClean = Math.min(startSeq, minActiveSeq);

  for (const [id, mut] of Array.from(map.entries())) {
    // Preservar tombstones para proteger contra ressuscitação de deletes em páginas tardias
    if (mut.type !== 'delete' && mut.seq <= maxSafeSeqToClean) {
      map.delete(id);
    }
  }
}

export function reconcileServerItems(storageKey: string, serverItems: Message[], startSeq: number): Message[] {
  const map = mutationsByKey.get(storageKey);
  const tombstones = tombstonesByKey.get(storageKey);

  const resultItems: Message[] = [];
  const handledIds = new Set<string>();

  for (const item of serverItems) {
    if (!item || !item.id) continue;

    // Tombstones garantem que deletados confirmados nunca ressuscitem
    if (tombstones && tombstones.has(item.id)) {
      handledIds.add(item.id);
      continue;
    }

    if (map) {
      const mut = map.get(item.id);
      if (mut) {
        if (mut.type === 'delete') {
          handledIds.add(item.id);
          continue;
        }
        if (mut.type === 'update') {
          // Se a mutação ocorreu DEPOIS do início deste fetch, override local vence
          if (mut.seq > startSeq) {
            const merged = { ...item, ...mut.data };
            resultItems.push(merged);
          } else {
            // Fetch iniciado após a mutação -> servidor é autoritativo
            resultItems.push(item);
          }
          handledIds.add(item.id);
          continue;
        }
        if (mut.type === 'insert') {
          if (mut.seq > startSeq) {
            const merged = { ...item, ...mut.data };
            resultItems.push(merged);
          } else {
            resultItems.push(item);
          }
          handledIds.add(item.id);
          continue;
        }
      }
    }

    resultItems.push(item);
    handledIds.add(item.id);
  }

  // Incluir qualquer INSERT registrado DEPOIS do início do fetch e ausente da resposta
  if (map) {
    for (const mut of map.values()) {
      if (mut.type === 'insert' && mut.seq > startSeq && !handledIds.has(mut.id) && mut.data) {
        if (tombstones && tombstones.has(mut.id)) continue;
        resultItems.push(mut.data);
        handledIds.add(mut.id);
      }
    }
  }

  return resultItems;
}

// Consolidador direto das páginas do TanStack Query + mutações locais ativas (SEM cache paralelo)
export function getConsolidatedMessages(
  storageKey: string,
  pages?: { items: Message[] }[]
): Message[] {
  if (isContextRevoked(storageKey)) return [];

  const map = mutationsByKey.get(storageKey);
  const tombstones = tombstonesByKey.get(storageKey);
  const activeMutations = map ? new Map(map) : null;
  const msgMap = new Map<string, Message>();

  // 1. Processa páginas autoritativas do TanStack Query
  if (pages) {
    for (const page of pages) {
      if (page?.items) {
        for (const msg of page.items) {
          if (!msg || !msg.id) continue;
          if (tombstones && tombstones.has(msg.id)) continue;
          if (activeMutations) {
            const mut = activeMutations.get(msg.id);
            if (mut) {
              if (mut.type === 'delete') continue;
              if (mut.type === 'update') {
                msgMap.set(msg.id, { ...msg, ...mut.data });
                continue;
              }
            }
          }
          msgMap.set(msg.id, msg);
        }
      }
    }
  }

  // 2. Adiciona inserts ativos ainda não consolidados nas páginas
  if (activeMutations) {
    for (const mut of activeMutations.values()) {
      if (mut.type === 'insert' && mut.data && !msgMap.has(mut.id)) {
        if (tombstones && tombstones.has(mut.id)) continue;
        msgMap.set(mut.id, mut.data);
      }
    }
  }

  const list = Array.from(msgMap.values());
  list.sort(compareMessages);
  return list;
}

function getStorageKey(key: readonly any[]): string {
  return JSON.stringify(key);
}

/**
 * Executa a recuperação autoritativa cobrindo a fronteira histórica previamente carregada.
 * Valida o ciclo de vida e a geração antes de cada requisição e antes da publicação final.
 */
export async function recoverLeadMessages(
  leadId: string,
  options?: UseLeadMessagesOptions,
  customQueryClient = queryClient,
  callerGeneration?: number
): Promise<InfiniteData<MessagesPageResponse> | undefined> {
  const queryKey = buildMessagesQueryKey(leadId, options);
  const storageKey = getStorageKey(queryKey);

  const initialCtx = getContextLifecycle(storageKey);
  const opGen = callerGeneration ?? initialCtx.generation;

  if (initialCtx.isRevoked || !isOperationValid(storageKey, opGen)) {
    return undefined;
  }

  const recoveryId = ++globalRecoveryCounter;
  activeRecoveryByKey.set(storageKey, recoveryId);

  const currentCached = customQueryClient.getQueryData<InfiniteData<MessagesPageResponse>>(queryKey);
  let oldestKnownMsg: Message | null = null;
  let totalCachedCount = 0;

  if (currentCached?.pages && currentCached.pages.length > 0) {
    for (const page of currentCached.pages) {
      if (page?.items && page.items.length > 0) {
        totalCachedCount += page.items.length;
        for (const item of page.items) {
          if (item?.id) {
            if (!oldestKnownMsg || compareMessages(item, oldestKnownMsg) < 0) {
              oldestKnownMsg = item;
            }
          }
        }
      }
    }
  }

  const oldestKnownId = oldestKnownMsg?.id || null;

  const { fetchId, startSeq } = startFetchTracking(storageKey);
  const recoveredPages: MessagesPageResponse[] = [];
  const recoveredPageParams: (string | null)[] = [null];
  let nextCursor: string | null = null;
  let hasMore = true;
  let coveredOldest = false;

  try {
    while (hasMore) {
      // Revalida antes de cada requisição
      if (!isOperationValid(storageKey, opGen) || activeRecoveryByKey.get(storageKey) !== recoveryId) {
        finishFetchTracking(storageKey, fetchId, startSeq);
        return undefined;
      }

      const pageRes = await api.getMessagesPage(leadId, {
        cursor: nextCursor,
        limit: 50,
        lojaId: options?.lojaId,
        tenantId: options?.tenantId,
        signal: options?.signal
      });

      // Revalidação IMEDIATA após o await
      if (!isOperationValid(storageKey, opGen) || activeRecoveryByKey.get(storageKey) !== recoveryId) {
        finishFetchTracking(storageKey, fetchId, startSeq);
        return undefined;
      }

      recoveredPages.push(pageRes);

      // Verificação de cobertura da fronteira antiga:
      // 1. Pelo ID exato do item mais antigo
      if (oldestKnownId && pageRes.items?.some(m => m.id === oldestKnownId)) {
        coveredOldest = true;
      }

      // 2. Se o ID mais antigo foi excluído no servidor, reconhece por ordenação temporal
      if (oldestKnownMsg && !coveredOldest && pageRes.items && pageRes.items.length > 0) {
        const reachedOrPassed = pageRes.items.some(m => compareMessages(m, oldestKnownMsg!) <= 0);
        if (reachedOrPassed) {
          coveredOldest = true;
        }
      }

      // 3. Se o servidor chegou ao fim absoluto da conversa (hasMore: false), cobriu toda a história
      if (!pageRes.hasMore || !pageRes.nextCursor) {
        hasMore = false;
        coveredOldest = true;
        break;
      }

      // Se cobrimos a fronteira antiga conhecida, encerra a paginação sem varredura desnecessária
      if (oldestKnownId && coveredOldest) {
        hasMore = false;
        break;
      }

      // Se não havia histórico anterior, 1 página é suficiente
      if (!oldestKnownId) {
        hasMore = false;
        coveredOldest = true;
        break;
      }

      // Salvaguarda: se atingiu o limite antes de cobrir a fronteira, NUNCA publica como completa
      if (recoveredPages.length >= 20 || (totalCachedCount > 0 && recoveredPages.length * 50 >= totalCachedCount + 100)) {
        if (!coveredOldest) {
          finishFetchTracking(storageKey, fetchId, startSeq);
          throw new Error('Incomplete recovery: boundary not covered');
        }
        hasMore = false;
        break;
      }

      nextCursor = pageRes.nextCursor;
      recoveredPageParams.push(nextCursor);
    }

    if (!isOperationValid(storageKey, opGen) || activeRecoveryByKey.get(storageKey) !== recoveryId) {
      finishFetchTracking(storageKey, fetchId, startSeq);
      return undefined;
    }

    // Reconciliação atômica final: preserva mutações que chegaram durante as requisições entre páginas
    const finalizedPages: MessagesPageResponse[] = recoveredPages.map(page => ({
      ...page,
      items: reconcileServerItems(storageKey, page.items || [], startSeq)
    }));

    finishFetchTracking(storageKey, fetchId, startSeq);

    const fullResult: InfiniteData<MessagesPageResponse> = {
      pages: finalizedPages,
      pageParams: recoveredPageParams
    };

    customQueryClient.setQueryData<InfiniteData<MessagesPageResponse>>(queryKey, fullResult);
    return fullResult;
  } catch (err: any) {
    finishFetchTracking(storageKey, fetchId, startSeq);

    // Se o contexto foi revogado ou invalidado durante o fetch (ex: logout), descarta silenciosamente sem alterar nova sessão
    if (!isOperationValid(storageKey, opGen) || activeRecoveryByKey.get(storageKey) !== recoveryId) {
      return undefined;
    }

    const status = err?.status || err?.response?.status;
    if (status === 401 || status === 403) {
      revokeContext(storageKey, status, status === 401 ? 'Não autorizado' : 'Acesso negado', leadId);
      customQueryClient.removeQueries({ queryKey, exact: true });
      customQueryClient.setQueryData(queryKey, () => undefined);
    }
    throw err;
  }
}

export function useLeadMessages(leadId: string, options?: UseLeadMessagesOptions) {
  const userId = options?.userId ?? null;
  const lojaId = options?.lojaId ?? null;
  const tenantId = options?.tenantId ?? null;
  const filtersStr = options?.filters ? JSON.stringify(options.filters) : '';

  const queryKey = useMemo(
    () => buildMessagesQueryKey(leadId, options),
    [leadId, userId, lojaId, tenantId, filtersStr]
  );
  const storageKey = useMemo(() => getStorageKey(queryKey), [queryKey]);

  // Ref com as opções mais recentes para uso em callbacks sem recriar referências
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Context lifecycle & generation
  const contextKey = `${leadId || ''}:${userId || ''}:${lojaId || ''}:${tenantId || ''}`;
  const [accessDeniedError, setAccessDeniedError] = useState<{ status: number; message: string } | null>(null);
  const [syncError, setSyncError] = useState<any>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);

  // Referência de contexto para verificar validade em tempo real de execução
  const lifecycleRef = useRef({
    contextKey,
    storageKey,
    generation: getContextLifecycle(storageKey).generation
  });

  // Atualização ou transição de contexto
  const prevContextKeyRef = useRef(contextKey);
  useEffect(() => {
    if (prevContextKeyRef.current !== contextKey) {
      setOptimisticMessages([]);
      setAccessDeniedError(null);
      setSyncError(null);
      prevContextKeyRef.current = contextKey;
      const ctx = getContextLifecycle(storageKey);
      lifecycleRef.current = {
        contextKey,
        storageKey,
        generation: ctx.generation
      };
    }
  }, [contextKey, storageKey]);

  const currentLifecycle = getContextLifecycle(storageKey);
  const isRevoked = isContextRevoked(storageKey);
  const currentGeneration = currentLifecycle.generation;

  // Sincroniza accessDeniedError com o registro central de lifecycle de forma estável
  const effectiveAccessDenied = useMemo(() => {
    if (accessDeniedError) return accessDeniedError;
    if (isRevoked) {
      return {
        status: currentLifecycle.status || 403,
        message: currentLifecycle.message || (currentLifecycle.status === 401 ? 'Não autorizado' : 'Acesso negado')
      };
    }
    return null;
  }, [accessDeniedError, isRevoked, currentLifecycle.status, currentLifecycle.message]);

  const isEnabled = Boolean(leadId && userId) && !effectiveAccessDenied && !isRevoked;

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      // Verificação em tempo de execução
      if (!leadId || !options?.userId || isContextRevoked(storageKey) || effectiveAccessDenied) {
        return { items: [], nextCursor: null, hasMore: false };
      }

      const { fetchId, startSeq } = startFetchTracking(storageKey);
      try {
        const res = await api.getMessagesPage(leadId, {
          cursor: pageParam as string | null,
          limit: 50,
          lojaId: options?.lojaId,
          tenantId: options?.tenantId,
          signal
        });

        // Se o contexto foi revogado enquanto o fetch estava em voo, descarta
        if (isContextRevoked(storageKey) || !isOperationValid(storageKey, currentGeneration)) {
          finishFetchTracking(storageKey, fetchId, startSeq);
          return { items: [], nextCursor: null, hasMore: false };
        }

        // Reconciliação temporal com base no início do fetch
        const reconciledItems = reconcileServerItems(storageKey, res.items || [], startSeq);
        finishFetchTracking(storageKey, fetchId, startSeq);

        return {
          ...res,
          items: reconciledItems
        };
      } catch (err: any) {
        finishFetchTracking(storageKey, fetchId, startSeq);
        const status = err?.status || err?.response?.status;
        if (status === 401 || status === 403) {
          const authErr = {
            status,
            message: status === 401 ? 'Não autorizado' : 'Acesso negado'
          };
          setAccessDeniedError(authErr);
          setOptimisticMessages([]);
          revokeContext(storageKey, status, authErr.message, leadId);
          queryClient.removeQueries({ queryKey, exact: true });
          queryClient.setQueryData(queryKey, () => undefined);
        }
        throw err;
      }
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: MessagesPageResponse) => {
      return lastPage?.hasMore && lastPage?.nextCursor ? lastPage.nextCursor : undefined;
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 10,   // 10 minutos
    retry: (failureCount, error: any) => {
      const status = error?.status || error?.response?.status;
      if (status === 401 || status === 403 || status === 404 ||
          error?.name === 'TimeoutError' || error?.name === 'AbortError') return false;
      return failureCount < 2;
    }
  });

  // Mensagens remotas a partir das páginas do TanStack Query
  const remoteMessages = useMemo(() => {
    if (effectiveAccessDenied || isRevoked) return [];
    return getConsolidatedMessages(storageKey, query.data?.pages);
  }, [query.data?.pages, storageKey, effectiveAccessDenied, isRevoked]);

  // Reconciliação de mensagens otimistas contra o cache remoto confirmado
  const reconciledOptimistic = useMemo(() => {
    if (effectiveAccessDenied || isRevoked || optimisticMessages.length === 0) return [];
    const map = mutationsByKey.get(storageKey);
    const tombstones = tombstonesByKey.get(storageKey);
    const remoteIdSet = new Set(remoteMessages.map(m => m.id));
    const remoteExtIdSet = new Set(
      remoteMessages.map(m => (m as any).external_message_id).filter(Boolean)
    );

    return optimisticMessages.filter(opt => {
      if (tombstones && tombstones.has(opt.id)) return false;
      if (map) {
        const mut = map.get(opt.id);
        if (mut && mut.type === 'delete') return false;
      }
      if (remoteIdSet.has(opt.id)) return false;
      if ((opt as any).external_message_id && remoteExtIdSet.has((opt as any).external_message_id)) return false;
      return true;
    });
  }, [optimisticMessages, remoteMessages, storageKey, effectiveAccessDenied, isRevoked]);

  // Histórico consolidado final para renderização (sempre ordenado cronologicamente)
  const messages = useMemo(() => {
    if (effectiveAccessDenied || isRevoked) return [];
    const list = [...remoteMessages, ...reconciledOptimistic];
    list.sort(compareMessages);
    return list;
  }, [remoteMessages, reconciledOptimistic, effectiveAccessDenied, isRevoked, storageKey]);

  // Callbacks com validação em tempo de execução contra geração e revogação
  const myGeneration = currentGeneration;

  const addRealtimeMessage = useCallback((msg: Message) => {
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
      return;
    }

    const tombstones = tombstonesByKey.get(storageKey);
    if (tombstones && tombstones.has(msg.id)) {
      return; // Deletado confirmado não ressuscita
    }

    const map = mutationsByKey.get(storageKey);
    if (map) {
      const mut = map.get(msg.id);
      if (mut && mut.type === 'delete') return;
    }

    recordMutation(storageKey, msg.id, 'insert', msg);

    queryClient.setQueryData<InfiniteData<MessagesPageResponse>>(queryKey, (old) => {
      if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
        return undefined as any;
      }

      const currentTombstones = tombstonesByKey.get(storageKey);
      if (currentTombstones && currentTombstones.has(msg.id)) {
        return old;
      }

      if (!old || !old.pages || old.pages.length === 0) {
        return {
          pages: [{ items: [msg], nextCursor: null, hasMore: false }],
          pageParams: [null]
        };
      }

      const exists = old.pages.some(p => p.items.some(m => m.id === msg.id));
      if (exists) {
        return {
          ...old,
          pages: old.pages.map(p => ({
            ...p,
            items: p.items.map(m => m.id === msg.id ? { ...m, ...msg } : m)
          }))
        };
      }

      const newPages = [...old.pages];
      newPages[0] = {
        ...newPages[0],
        items: [...newPages[0].items, msg]
      };

      return {
        ...old,
        pages: newPages
      };
    });

    setOptimisticMessages(prev => prev.filter(opt => {
      if (opt.id === msg.id) return false;
      if ((opt as any).external_message_id && (opt as any).external_message_id === (msg as any).external_message_id) return false;
      return true;
    }));
  }, [queryKey, storageKey, myGeneration]);

  const updateMessageInCache = useCallback((id: string, patch: Partial<Message>) => {
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
      return;
    }

    const tombstones = tombstonesByKey.get(storageKey);
    if (tombstones && tombstones.has(id)) {
      return;
    }

    recordMutation(storageKey, id, 'update', patch);

    queryClient.setQueryData<InfiniteData<MessagesPageResponse>>(queryKey, (old) => {
      if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
        return undefined as any;
      }
      if (!old || !old.pages) return old;
      return {
        ...old,
        pages: old.pages.map(page => ({
          ...page,
          items: page.items.map(m => m.id === id ? { ...m, ...patch } : m)
        }))
      };
    });

    setOptimisticMessages(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
  }, [queryKey, storageKey, myGeneration]);

  const removeMessageFromCache = useCallback((id: string) => {
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
      return;
    }

    recordMutation(storageKey, id, 'delete');

    queryClient.setQueryData<InfiniteData<MessagesPageResponse>>(queryKey, (old) => {
      if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
        return undefined as any;
      }
      if (!old || !old.pages) return old;
      return {
        ...old,
        pages: old.pages.map(page => ({
          ...page,
          items: page.items.filter(m => m.id !== id)
        }))
      };
    });

    setOptimisticMessages(prev => prev.filter(m => m.id !== id));
  }, [queryKey, storageKey, myGeneration]);

  const rollbackDeletedMessage = useCallback((msg: Message) => {
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
      return;
    }

    recordMutation(storageKey, msg.id, 'rollback_delete', msg);

    queryClient.setQueryData<InfiniteData<MessagesPageResponse>>(queryKey, (old) => {
      if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
        return undefined as any;
      }
      if (!old || !old.pages || old.pages.length === 0) {
        return {
          pages: [{ items: [msg], nextCursor: null, hasMore: false }],
          pageParams: [null]
        };
      }
      const exists = old.pages.some(p => p.items.some(m => m.id === msg.id));
      if (exists) {
        return old;
      }
      const newPages = [...old.pages];
      newPages[0] = {
        ...newPages[0],
        items: [msg, ...newPages[0].items]
      };
      return {
        ...old,
        pages: newPages
      };
    });

    setOptimisticMessages(prev => [...prev]);
  }, [queryKey, storageKey, myGeneration]);

  const addOptimisticMessage = useCallback((msg: Message) => {
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
      return;
    }
    setOptimisticMessages(prev => [...prev.filter(m => m.id !== msg.id), msg]);
  }, [storageKey, myGeneration]);

  const replaceOptimisticWithReal = useCallback((tempId: string, realMsg: Message) => {
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
      return;
    }
    addRealtimeMessage(realMsg);
    setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
  }, [addRealtimeMessage, storageKey, myGeneration]);

  const markOptimisticFailed = useCallback((tempId: string, error: string) => {
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
      return;
    }
    setOptimisticMessages(prev => prev.map(m => {
      if (m.id === tempId) {
        return {
          ...m,
          id: `failed-${Date.now()}-${tempId}`,
          send_status: 'failed',
          error
        };
      }
      return m;
    }));
  }, [storageKey, myGeneration]);

  const queryRef = useRef(query);
  queryRef.current = query;

  const loadMore = useCallback(async () => {
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, myGeneration)) {
      return;
    }
    const q = queryRef.current;
    if (q.hasNextPage && !q.isFetchingNextPage) {
      await q.fetchNextPage();
    }
  }, [storageKey, myGeneration]);

  const refetch = useCallback(async (refetchOpts?: any) => {
    const currentGen = getContextLifecycle(storageKey).generation;
    if (isContextRevoked(storageKey) || !isOperationValid(storageKey, currentGen)) {
      const err = effectiveAccessDenied || { status: 403, message: 'Acesso negado' };
      return {
        data: undefined,
        error: err,
        isError: true,
        isLoading: false,
        isSuccess: false
      };
    }

    try {
      // V6: Recuperação autoritativa com geração capturada
      const res = await recoverLeadMessages(leadId, optionsRef.current, queryClient, currentGen);
      setSyncError(null);
      setOptimisticMessages(prev => [...prev]);
      return {
        data: res,
        error: null,
        isError: false,
        isLoading: false,
        isSuccess: true
      };
    } catch (err: any) {
      const status = err?.status || err?.response?.status;
      if (status === 401 || status === 403) {
        const authErr = {
          status,
          message: status === 401 ? 'Não autorizado' : 'Acesso negado'
        };
        setSyncError(null);
        setAccessDeniedError(authErr);
        setOptimisticMessages([]);
        revokeContext(storageKey, status, authErr.message, leadId);
        queryClient.removeQueries({ queryKey, exact: true });
        queryClient.setQueryData(queryKey, () => undefined);
      } else {
        // NÃO revoga o contexto (usuário continua autorizado).
        // NÃO limpa cache, NÃO zera mensagens visíveis.
        // Expor o erro no estado consumido pela interface:
        setSyncError(err);
      }
      return {
        data: undefined,
        error: err,
        isError: true,
        isLoading: false,
        isSuccess: false
      };
    }
  }, [leadId, storageKey, queryKey, effectiveAccessDenied]);

  const effectiveError = effectiveAccessDenied || syncError || query.error;

  return {
    messages,
    isLoading: isEnabled ? query.isLoading : false,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasMore: isRevoked || effectiveAccessDenied ? false : Boolean(query.hasNextPage),
    isError: Boolean(isRevoked || effectiveAccessDenied || syncError) || query.isError,
    error: effectiveError,
    loadMore,
    refetch,
    addRealtimeMessage,
    updateMessageInCache,
    removeMessageFromCache,
    rollbackDeletedMessage,
    addOptimisticMessage,
    replaceOptimisticWithReal,
    markOptimisticFailed
  };
}
